import type {
    PeriodicIntersectionDomain2D,
} from './AbstractPeriodicIntersectionSolver';
import type {CircularArcIntersectionDefinition2D} from './ArcCircleIntersectionSolver';
import {
    EllipseCircleIntersectionSolver,
    type EllipseCircleIntersection2D,
    type EllipseIntersectionDefinition2D,
} from './EllipseCircleIntersectionSolver';

/**
 * 二维部分椭圆求交定义。
 *
 * 该定义属于圆弧—椭圆子求解器，周期抽象层无需了解具体椭圆数据结构。
 */
export interface EllipticalArcIntersectionDefinition2D
    extends EllipseIntersectionDefinition2D, PeriodicIntersectionDomain2D {}

/**
 * 圆弧与椭圆弧的单个真实交点。
 */
export interface ArcEllipseIntersection2D extends EllipseCircleIntersection2D {
    /** 交点在圆弧有效域内的局部参数，范围 `[0, 1]`。 */
    arcParameter: number;
    /** 交点在椭圆有效域内的局部参数，范围 `[0, 1]`。 */
    ellipseParameter: number;
}

/**
 * 圆弧与椭圆弧的真实交点求解器。
 *
 * 父类负责完整椭圆与支撑圆的四次方程求解；本类只过滤圆弧域和部分椭圆域，
 * 因而完整椭圆、部分椭圆都共享同一套真实数学交点。
 */
export class ArcEllipseIntersectionSolver extends EllipseCircleIntersectionSolver {
    /**
     * 求解圆弧与椭圆弧的全部真实交点。
     *
     * @param arc 圆弧定义。
     * @param ellipse 完整或部分椭圆定义。
     * @returns 同时位于圆弧和椭圆有效域内的真实交点。
     */
    public solveArcEllipse(
        arc: CircularArcIntersectionDefinition2D,
        ellipse: EllipticalArcIntersectionDefinition2D,
    ): ArcEllipseIntersection2D[] {
        return super.solve(ellipse, arc).flatMap((intersection) => {
            // 支撑曲线求解完成后，必须同时过滤双方的有限周期域。
            const arcParameter = this.projectAngleToDomainParameter(intersection.circleAngle, arc);
            const ellipseParameter = this.projectAngleToDomainParameter(
                intersection.ellipseAngle,
                ellipse,
            );
            return arcParameter === null || ellipseParameter === null
                ? []
                : [{...intersection, arcParameter, ellipseParameter}];
        });
    }
}
